import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../data/translations';
import axios from 'axios';
import { LogOut, ArrowLeft, Inbox, Eye, CheckCircle, Trash2, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { lang } = useLanguage();
  const t = translations.admin[lang];
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, answered: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (data.role !== 'admin') {
        navigate('/admin');
        return;
      }
      setUser(data);
    } catch {
      navigate('/admin');
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [inqRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/inquiries/stats`, { withCredentials: true })
      ]);
      setInquiries(inqRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth().then(fetchData);
  }, [checkAuth, fetchData]);

  const updateStatus = async (inquiry, status) => {
    try {
      await axios.patch(
        `${API}/admin/inquiries/${encodeURIComponent(inquiry.email)}/${encodeURIComponent(inquiry.created_at)}`,
        { status },
        { withCredentials: true }
      );
      fetchData();
    } catch (err) {
      console.error('Update error:', err);
    }
  };

  const deleteInquiry = async (inquiry) => {
    if (!window.confirm(lang === 'de' ? 'Wirklich löschen?' : 'Eliminare davvero?')) return;
    try {
      await axios.delete(
        `${API}/admin/inquiries/${encodeURIComponent(inquiry.email)}/${encodeURIComponent(inquiry.created_at)}`,
        { withCredentials: true }
      );
      setSelectedInquiry(null);
      fetchData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate('/admin');
  };

  const statusColors = {
    new: 'bg-blue-500/20 text-blue-400',
    read: 'bg-yellow-500/20 text-yellow-400',
    answered: 'bg-green-500/20 text-green-400'
  };

  if (!user) return null;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0d1b2a]">
      {/* Header */}
      <header className="bg-enbi-navy border-b border-white/10 px-6 md:px-12 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo-monogram-white.png" alt="ENBI" className="w-8 h-8" />
            <span className="font-outfit text-white font-bold tracking-wider text-sm">{t.dashboard}</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-white/40 text-xs tracking-wider uppercase hover:text-enbi-gold transition-colors flex items-center gap-2"
              data-testid="admin-back-btn"
            >
              <ArrowLeft size={14} />
              {t.back}
            </button>
            <button
              onClick={handleLogout}
              className="text-white/40 text-xs tracking-wider uppercase hover:text-red-400 transition-colors flex items-center gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} />
              {t.logout}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: t.total, value: stats.total, icon: Inbox, color: 'text-white' },
            { label: t.new, value: stats.new, icon: MessageSquare, color: 'text-blue-400' },
            { label: t.read, value: stats.read, icon: Eye, color: 'text-yellow-400' },
            { label: t.answered, value: stats.answered, icon: CheckCircle, color: 'text-green-400' },
          ].map((stat, i) => (
            <div key={i} className="bg-white/[0.04] border border-white/10 p-5" data-testid={`stat-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <stat.icon className={stat.color} size={18} strokeWidth={1.25} />
                <span className="text-white/50 text-xs tracking-wider uppercase">{stat.label}</span>
              </div>
              <div className={`font-outfit text-2xl font-bold ${stat.color}`}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Inquiries list */}
        <div className="bg-white/[0.04] border border-white/10">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="font-outfit text-white text-sm tracking-wider uppercase font-semibold">{t.inquiries}</h2>
          </div>

          {loading ? (
            <div className="p-12 text-center text-white/30 text-sm">{lang === 'de' ? 'Laden...' : 'Caricamento...'}</div>
          ) : inquiries.length === 0 ? (
            <div className="p-12 text-center text-white/30 text-sm" data-testid="no-inquiries">{t.noInquiries}</div>
          ) : (
            <div className="divide-y divide-white/5">
              {inquiries.map((inq, i) => (
                <div
                  key={i}
                  className={`px-6 py-4 hover:bg-white/[0.02] cursor-pointer transition-colors ${selectedInquiry === i ? 'bg-white/[0.04]' : ''}`}
                  onClick={() => setSelectedInquiry(selectedInquiry === i ? null : i)}
                  data-testid={`inquiry-row-${i}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-white font-semibold text-sm">{inq.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 tracking-wider uppercase font-semibold ${statusColors[inq.status] || statusColors.new}`}>
                        {inq.status}
                      </span>
                    </div>
                    <span className="text-white/30 text-xs">
                      {new Date(inq.created_at).toLocaleDateString(lang === 'de' ? 'de-DE' : 'it-IT')}
                    </span>
                  </div>
                  <div className="text-white/40 text-xs">{inq.email} {inq.company && `| ${inq.company}`}</div>

                  {selectedInquiry === i && (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      {inq.phone && <p className="text-white/50 text-xs mb-1">Tel: {inq.phone}</p>}
                      {inq.service && <p className="text-white/50 text-xs mb-3">Service: {inq.service}</p>}
                      <p className="text-white/70 text-sm leading-relaxed mb-4 whitespace-pre-wrap">{inq.message}</p>
                      <div className="flex gap-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); updateStatus(inq, 'read'); }}
                          className="text-xs tracking-wider uppercase text-yellow-400 hover:text-yellow-300 border border-yellow-400/30 px-3 py-1.5 transition-colors"
                          data-testid={`mark-read-${i}`}
                        >
                          {t.markRead}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); updateStatus(inq, 'answered'); }}
                          className="text-xs tracking-wider uppercase text-green-400 hover:text-green-300 border border-green-400/30 px-3 py-1.5 transition-colors"
                          data-testid={`mark-answered-${i}`}
                        >
                          {t.markAnswered}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteInquiry(inq); }}
                          className="text-xs tracking-wider uppercase text-red-400 hover:text-red-300 flex items-center gap-1"
                          data-testid={`delete-inquiry-${i}`}
                        >
                          <Trash2 size={12} />
                          {t.delete}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
